<script setup lang="ts">
import type { NavFolderItemProps } from "~/types/nav.d"

const props = defineProps<NavFolderItemProps>()

function getSubjectsInFolder(id: number) {
  return props.data.filter(i => i.parentLocationId === id)
}

const route = useRoute()
const hash = computed(() => formatForURL(props.folder.title))

const isOpen = computed(() => {
  return route.hash.slice(1) === hash.value
})

function closeIfOpen(event: Event) {
  if (isOpen.value) {
    navigateTo({ hash: "" }, { replace: true })
    event.preventDefault()
  }
}

const folderContent = computed(() =>
  getSubjectsInFolder(props.folder.id),
)

const mediaCounts = useMediaCounts(() => props.folder)

const folderHasContent = useHasContent(
  mediaCounts.data,
  () => folderContent.value.some(f => f.hasIntroPages),
)
</script>

<template>
  <NuxtLink
    class="folder-btn flex w-full items-center justify-between pl-3 hover:text-u-contrast"
    text-class="!whitespace-normal text-left"
    left-align
    data-pendo="folder-list-item"
    :class="[
      {
        opened: isOpen,
        'pointer-events-none opacity-60':
          folderContent && !folderHasContent,
      },
    ]"
    :to="{ hash: `#${hash}` }"
    @click="closeIfOpen($event)"
  >
    <div
      class="flex max-w-[160px] gap-3 text-xl font-extrabold leading-6 md:max-w-[180px] lg:max-w-[250px] xl:max-w-[300px]"
    >
      <span> {{ folder.priority }}</span>
      <h3>
        {{ folder.title }}
      </h3>
    </div>

    <UButton
      v-if="folderHasContent"
      tabindex="-1"
      aria-disabled="true"
      theme="white"
      size="sm"
      variant="quite"
      :aria-expanded="isOpen"
      aria-controls="folderContentList"
    >
      <UIcon
        :name="isOpen ? 'chevron-up' : 'chevron-down'"
        size="sm"
      />
    </UButton>
  </NuxtLink>
  <ul v-if="isOpen" id="folderContentList">
    <li
      v-for="(i, key) in folderContent"
      :key="key"
      class="folder-subject"
    >
      <UNavListItem
        :item="i"
        :page-type="pageType"
        :licence="licence"
      />
    </li>
  </ul>
</template>

<style scoped lang="postcss">
.folder-btn {
  @apply flex w-full gap-3 py-3 font-national2 text-[22px] font-bold;
  @apply flex border-b-2 border-white border-opacity-20;

  &.opened {
    @apply border-b-2 border-opacity-100;
  }
}
.folder-subject:last-child {
  @apply pb-6;
}
</style>
